<template>
  <div
    class="relative mx-auto max-w-[var(--breakpoint-2xl)] px-6 sm:px-8"
    :class="[blok.disable_top_padding ? '' : '-mt-8 sm:-mt-16']"
  >
    <UTabs
      v-model="activeTab"
      :items="blok.tabs"
      :ui="{
        root: 'px-2 sm:px-0',
        list: 'bg-neutral-900/40 ring ring-yellow-400 backdrop-blur-lg max-w-xl z-[10] max-auto sticky top-28 lg:top-40',
        leadingIcon: 'hidden sm:block',
        trigger: 'disabled:!text-neutral-600 disabled:text-xs',
        label: 'font-bold',
        content: 'pt-8 space-y-16 md:space-y-24',
      }"
      @update:model-value="updateRouteParams"
    >
      <template #content="{ item: tabItem }">
        <StoryblokComponent
          v-for="nestedItem in tabItem.body"
          :key="nestedItem._uid"
          :blok="nestedItem"
        />
      </template>
    </UTabs>
  </div>
</template>

<script setup lang="ts">
import type { LayoutTabsStoryblok } from '~/types/component-types-sb';

const route = useRoute();
const router = useRouter();
const { slugify } = useSlugify();

const props = defineProps<{
  blok: LayoutTabsStoryblok;
  uuid?: string;
}>();

const activeTab = ref('0');

// Set active tab based on query parameter
const setActiveTabFromQuery = () => {
  const queryParam = route.query.tab;
  if (queryParam && typeof queryParam === 'string' && props.blok.tabs) {
    const tabIndex = props.blok.tabs.findIndex(
      (tab) => slugify(tab.label || '') === queryParam
    );
    if (tabIndex !== -1) {
      activeTab.value = tabIndex.toString();
    } else {
      // If no matching tab found, navigate to root URL
      router.push({ query: {} });
    }
  }
};

onMounted(() => {
  setActiveTabFromQuery();
});

// Update query parameters when tab changes
const updateRouteParams = (newTabValue: string | number) => {
  const tabIndex =
    typeof newTabValue === 'number' ? newTabValue : parseInt(newTabValue);
  const selectedTab = props.blok.tabs?.[tabIndex];
  if (!selectedTab?.label) return;

  const tabSlug = slugify(selectedTab.label);

  // Update the route query parameter and navigate
  router.push({
    query: {
      ...route.query, // Keep other query params
      tab: tabSlug,
    },
  });

  // Scroll to the top of the section
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Watch for changes in query parameters
watch(
  () => route.query.tab,
  () => {
    setActiveTabFromQuery();
  }
);
</script>
